/**
 * The decision sheet (screens §2.5): a drawer with four short sections, opened from the decision's label on Decide, from a Trace card and from the Bank tab's "Decisions that move this".
 * What it does (with this round's own numbers) · The trade-off (now / later) · How a banker decides (with the live context and the consensus outlook) · What it moved last time.
 * It reads the catalog entry (`whatItDoes`, `tradeOffs`, `businessCase` when the catalog has them, else `help` and the deferred-cost notes), the round-start position and the last released round's decision attribution.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { ContentCatalog, DecisionCatalogEntry, MarketView, TeamState } from "@bsim/contracts";
import { useCoach } from "../coach/CoachDrawer";
import { Link, useLocation } from "../shell/history";
import { Drawer } from "../theme/Drawer";
import { GxDescribed } from "../theme/GxTerm";
import type { RiskProjection } from "../api/riskFields";
import { decisionIdOf } from "./decisionValues";
import { DecisionSheetBody } from "./DecisionSheetBody";
import "./decisions.css";

interface DecisionSheetApi {
  readonly open: (path: string) => void; /** The catalog's decisions, so a Trace card can word the effect table for its own decision. */ readonly entries: readonly DecisionCatalogEntry[];
  /** What the Treasury rate-position block reads beside the control: the round's team (its draft and the no-change seed), the catalog and the on-screen risk projection. */
  readonly team: TeamState; readonly catalog: ContentCatalog; readonly projection: RiskProjection | undefined;
}
const SheetContext = createContext<DecisionSheetApi | null>(null);

/** The way to open the sheet for a decision (or a decision path such as "deposits.retailMm.rate.front"); null outside a `DecisionSheetProvider`. */
export const useDecisionSheet = (): DecisionSheetApi | null => useContext(SheetContext);

export function DecisionSheetProvider({ team, catalog, market, projection, children }: { readonly team: TeamState; readonly catalog: ContentCatalog; readonly market: MarketView; readonly projection?: RiskProjection | undefined; readonly children: ReactNode }) {
  const [decisionId, open] = useState<string | null>(null);
  const api = useMemo<DecisionSheetApi>(() => ({ open: (path) => open(decisionIdOf(path)), entries: catalog.decisions, team, catalog, projection }), [catalog, team, projection]);
  const entry = decisionId === null ? undefined : catalog.decisions.find((d) => d.decisionId === decisionId);
  return (
    <SheetContext.Provider value={api}>
      {children}
      {entry === undefined ? null : <DecisionSheet entry={entry} team={team} catalog={catalog} market={market} projection={projection} onClose={() => open(null)} />}
    </SheetContext.Provider>
  );
}

function DecisionSheet({ entry, team, catalog, market, projection, onClose }: { readonly projection?: RiskProjection | undefined; readonly entry: DecisionCatalogEntry; readonly team: TeamState; readonly catalog: ContentCatalog; readonly market: MarketView; readonly onClose: () => void }) {
  const card = catalog.conceptCards.find((c) => c.id === entry.coachCardIds[0]);
  const onDecide = useLocation().pathname.includes("/decide");
  const coach = useCoach();
  const footer = (
    <div className="gx-sheet-f">
      {card === undefined ? null : <GxDescribed label={card.title} plain={card.summary} whyItMatters={card.whyItMatters} conceptCardId={card.id}>Read the idea behind it</GxDescribed>}
      <button type="button" className="gx-linkrow" data-testid="decision.sheet.coach" onClick={() => coach.open({ screen: "decide", label: entry.label, decisionId: entry.decisionId })}>Ask the Coach about this</button>
      {onDecide ? null : <Link href={`/c/${team.cohort.id}/decide`} className="gx-linkrow" data-testid="decision.sheet.decide">Change it on Decide</Link>}
    </div>
  );
  return (
    <Drawer title={entry.label} onClose={onClose} footer={footer}>
      <DecisionSheetBody entry={entry} team={team} catalog={catalog} market={market} projection={projection} panel={!onDecide} />
    </Drawer>
  );
}
